/**
 * Parses the given URL string and returns the normalized domain (hostname without www, port, path, or query).
 * @param urlString The URL string to parse.
 * @returns The domain if the URL is valid, otherwise null.
 */
export function parseDomain(urlString: string): string | null {
	if (!urlString || typeof urlString !== "string") {
		return null;
	}

	if (urlString.includes(" ") || urlString.includes("\n") || urlString.includes("\r") || urlString.includes("\t")) {
		return null;
	}

	try {
		if (!urlString.includes("://")) {
			urlString = `https://${urlString}`;
		}

		const url = new URL(urlString);
		const parts = decodeURIComponent(url.hostname).split(".");

		if (parts[0] === "www") {
			parts.shift();
		}

		if (parts.length < 2) {
			throw new Error("A domain cannot be a single word");
		}

		for (let i = 0; i < parts.length; i++) {
			if (!parts[i].includes("*")) {
				continue;
			}
			if (parts[i].length > 1 || i !== 0) {
				throw new Error("An asterisk can only be used in the first part of the domain and must be the only character in the subdomain");
			}
		}

		const tld = parts[parts.length - 1];
		if (tld.length < 2 && tld !== "*") {
			throw new Error("A domain cannot have a TLD that is less than 2 characters unless it's a wildcard");
		}

		return parts.join(".");
	} catch (error) {
		console.error("Error parsing domain", error);
		return null;
	}
}

/**
 * Validates if a URL is suitable for Locked Focus:
 * - Must start with http:// or https://
 * - Must identify a specific page: either a path beyond root, or root with query string, or client-side router (/#/)
 * - Root-only URLs with no query (e.g. https://example.com/) are not lockable to avoid locking to a whole site
 */
export function isLockedFocusCompatibleUrl(url?: string): boolean {
	if (!url) {
		return false;
	}

	try {
		const parsed = new URL(url);

		// Must use http or https
		if (!["http:", "https:"].includes(parsed.protocol)) {
			return false;
		}

		// Client-side router paths with hash in slash-hash-slash pattern (/#/) are valid
		if (parsed.hash.startsWith("#/")) {
			return true;
		}

		// Path beyond root (e.g. /page, /path/to/resource)
		if (parsed.pathname !== "/" && parsed.pathname.length > 1) {
			return true;
		}

		// Root path with query string identifies a specific page (e.g. Google Translate with params)
		if (parsed.pathname === "/" && parsed.search.length > 1) {
			return true;
		}

		return false;
	} catch {
		// Invalid URL format
		return false;
	}
}
